// ================================================
// CLARITY REPORTING — AGENCY PAGE
// Client management for Agency accounts
// ================================================

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Sidebar from '../components/common/Sidebar';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AgencyPage() {
  const { user } = useAuth();
  const [clients, setClients]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [adding,  setAdding]        = useState(false);
  const [form, setForm] = useState({
    client_name:          '',
    client_email:         '',
    client_company:       '',
    website_url:          '',
    send_report_to_client: true
  });

  const isAgencyPro = user?.account_type === 'agency_pro';
  const clientLimit = isAgencyPro ? Infinity : 20;

  useEffect(() => { fetchClients(); }, []);

  async function fetchClients() {
    try {
      const { data } = await api.get('/agency/clients');
      setClients(data);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddClient(e) {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post('/agency/clients', form);
      toast.success(`Client "${form.client_name}" added`);
      setForm({ client_name: '', client_email: '', client_company: '', website_url: '', send_report_to_client: true });
      setShowAdd(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add client');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveClient(clientId, clientName) {
    if (!window.confirm(`Remove client "${clientName}"? Their website data will be deactivated.`)) return;
    try {
      await api.delete(`/agency/clients/${clientId}`);
      toast.success('Client removed');
      fetchClients();
    } catch {
      toast.error('Failed to remove client');
    }
  }

  async function handleRunReport(websiteId) {
    try {
      await api.post(`/websites/${websiteId}/run-report`);
      toast.success('Report generation started');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start report');
    }
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="page-header">
          <div className="flex-between">
            <div>
              <h1>Client Management</h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 20 }}>
              <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
                {clients.length} / {isAgencyPro ? '∞' : 20} clients
              </span>
              {clients.length < clientLimit && (
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
                  + Add Client
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="page-container">

          {/* Add client form */}
          {showAdd && (
            <div className="card mb-32">
              <div className="card-header">
                <span className="card-title">Add New Client</span>
                <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
              </div>
              <form onSubmit={handleAddClient}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Client Name *</label>
                    <input
                      className="form-input" required
                      value={form.client_name}
                      onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                      placeholder="Acme Corp"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Client Company</label>
                    <input
                      className="form-input"
                      value={form.client_company}
                      onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))}
                      placeholder="Acme Corporation LLC"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Client Email</label>
                    <input
                      className="form-input" type="email"
                      value={form.client_email}
                      onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                      placeholder="contact@acmecorp.com"
                    />
                    <div className="form-hint">Reports will be sent here if provided.</div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Website URL *</label>
                    <input
                      className="form-input" required
                      value={form.website_url}
                      onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
                      placeholder="https://acmecorp.com"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <input
                    type="checkbox" id="send_to_client"
                    checked={form.send_report_to_client}
                    onChange={e => setForm(f => ({ ...f, send_report_to_client: e.target.checked }))}
                  />
                  <label htmlFor="send_to_client" style={{ fontSize: 13, cursor: 'pointer' }}>
                    Send weekly reports directly to client email
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={adding}>
                    {adding ? 'Adding...' : 'Add Client'}
                  </button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Clients table */}
          {loading ? (
            <div className="loading">Loading clients&hellip;</div>
          ) : clients.length === 0 ? (
            <div className="empty-state">
              <h3>No clients yet</h3>
              <p>Add your first client to start generating reports for them.</p>
              <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
                Add First Client
              </button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Website</th>
                    <th>Last Report</th>
                    <th style={{ textAlign: 'center' }}>Issues</th>
                    <th>Report Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => (
                    <tr key={client.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{client.client_name}</div>
                        {client.client_email && (
                          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
                            {client.client_email}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="text-mono text-small">{client.domain || client.url}</span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {client.last_report_date
                          ? new Date(client.last_report_date).toLocaleDateString()
                          : <span className="text-muted">Never</span>
                        }
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {client.problem_count > 0 ? (
                          <span style={{
                            background: 'var(--color-problem-bg)',
                            color: 'var(--color-problem-text)',
                            padding: '2px 8px', fontWeight: 700,
                            fontSize: 12
                          }}>
                            {client.problem_count} problems
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-gray-300)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td>
                        {client.send_report_to_client && client.client_email ? (
                          <span style={{ fontSize: 11, color: 'var(--color-success-text)', fontWeight: 600 }}>
                            ✓ Sending to client
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
                            You only
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {client.website_id && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleRunReport(client.website_id)}
                            >
                              Run Report
                            </button>
                          )}
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveClient(client.id, client.client_name)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Upgrade prompt if near limit */}
          {!isAgencyPro && clients.length >= 15 && (
            <div className="alert alert-warning mt-24">
              You're using {clients.length} of your 20 client slots.{' '}
              <a href="/settings" style={{ fontWeight: 700 }}>Upgrade to Agency Pro</a> for unlimited clients.
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
